import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Hero from "@/components/Hero";
import LoadingScreen from "@/components/LoadingScreen";
import Report from "@/components/Report";
import Pillars from "@/components/Pillars";
import ThreeAudiences from "@/components/ThreeAudiences";
import FAQSection from "@/components/FAQSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import RiesgoDemo from "@/components/RiesgoDemo";
import RubrosBand from "@/components/RubrosBand";
import UrgencyBanner from "@/components/UrgencyBanner";
import PressStrip from "@/components/PressStrip";
import PricingCards, { redirectToWebpay } from "@/components/PricingCards";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { FAQS } from "@/data/faqs";
import { toast } from "sonner";
import { Lock, Sparkles, ArrowRight, Infinity as InfinityIcon } from "lucide-react";
import { trackEvent, domainOf } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [stage, setStage] = useState("idle");
  const [result, setResult] = useState(null);
  const [submittedUrl, setSubmittedUrl] = useState("");
  const [scansLeft, setScansLeft] = useState(null); // null=desconocido, -1=ilimitado, 0..3 = restantes
  const [limitInfo, setLimitInfo] = useState(null);
  const heroRef = useRef(null);

  const handleScan = async (url) => {
    setSubmittedUrl(url);
    setStage("scanning");
    setResult(null);
    const domain = domainOf(url);
    trackEvent("scan_started", { domain });
    try {
      const res = await axios.post(`${API}/scan`, { url }, { withCredentials: true });
      const left = res.headers["x-scans-left"];
      setScansLeft(left === "unlimited" ? -1 : parseInt(left, 10));
      setResult(res.data);
      setStage("report");
      trackEvent("scan_completed", {
        domain,
        score: res.data?.score,
        riesgos_alto: res.data?.hallazgos?.filter((h) => h.riesgo === "alto").length || 0,
      });
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429 && typeof detail === "object" && detail?.code === "free_limit_reached") {
        setLimitInfo(detail);
        setStage("limit");
        trackEvent("scan_limit_reached", { domain });
        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
        return;
      }
      const msg = typeof detail === "string" ? detail : "No pudimos analizar tu sitio. Inténtalo otra vez.";
      toast.error(msg);
      trackEvent("scan_failed", { domain, error_code: e?.response?.status || "network" });
      setStage("idle");
    }
  };

  const handleReset = () => {
    setStage("idle");
    setResult(null);
    setSubmittedUrl("");
    setLimitInfo(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToScanner = () => {
    heroRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const homeSchemas = [
    orgSchema,
    breadcrumbSchema([{ name: "Inicio", path: "/" }]),
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Ley de Datos Chile",
      url: process.env.REACT_APP_BACKEND_URL,
      description:
        "Escáner gratuito y generador de documentos para cumplir con la Ley N° 21.719 de Protección de Datos Personales de Chile.",
      inLanguage: "es-CL",
      publisher: { "@type": "Organization", name: "Ley de Datos Chile" },
      potentialAction: {
        "@type": "SearchAction",
        target: `${process.env.REACT_APP_BACKEND_URL}/blog?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Ley de Datos Chile",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      inLanguage: "es-CL",
      availableOnDevice: ["desktop", "mobile"],
      countriesSupported: "CL",
      description:
        "Escáner web que analiza el cumplimiento de sitios chilenos con la Ley 21.719 de Protección de Datos Personales, y generador de documentos legales con IA.",
      offers: [
        { "@type": "Offer", price: "0", priceCurrency: "CLP", description: "Escáner gratuito" },
        { "@type": "Offer", price: "14990", priceCurrency: "CLP", description: "Acceso Pro — pago único permanente" },
      ],
      aggregateRating: undefined,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: "es-CL",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="El escáner de la Ley 21.719 · Protección de Datos Chile"
        description="Pega la URL de tu sitio y en 30 segundos te mostramos qué te falta para cumplir la Ley 21.719. Gratis, sin registro. Documentos legales por pago único de $14.990."
        path="/"
        keywords="Ley 21.719, Ley de Protección de Datos Personales Chile, escáner cumplimiento, política de privacidad Chile, APDP, GDPR Chile"
        ogImage="https://leydedatoschile.com/og/og-home.jpg"
        schemas={homeSchemas}
      />

      {stage === "idle" && (
        <>
          <UrgencyBanner />
          <div ref={heroRef}>
            <Hero onScan={handleScan} />
          </div>
          <PressStrip />
          <RubrosBand />
          <Pillars />
          <RiesgoDemo />
          <PricingCards onScrollToScanner={scrollToScanner} />
          <ThreeAudiences />
          <TestimonialsSection />
          <FAQSection />
          <ClosingCTA onScrollToScanner={scrollToScanner} />
        </>
      )}
      {stage === "scanning" && <LoadingScreen url={submittedUrl} />}
      {stage === "report" && result && (
        <>
          {scansLeft !== null && scansLeft >= 0 && scansLeft <= 1 && (
            <FreeQuotaBanner left={scansLeft} />
          )}
          <Report result={result} onReset={handleReset} />
        </>
      )}
      {stage === "limit" && limitInfo && (
        <LimitReachedPanel info={limitInfo} onReset={handleReset} />
      )}
    </div>
  );
}

function FreeQuotaBanner({ left }) {
  const isLast = left === 0;
  return (
    <div
      data-testid="free-quota-banner"
      className={`w-full ${isLast ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-blue-50 border-blue-200 text-blue-900"} border-y`}
    >
      <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-3">
        <Sparkles className={`w-4 h-4 shrink-0 ${isLast ? "text-amber-600" : "text-blue-600"}`} />
        <p className="text-sm flex-1">
          {isLast ? (
            <>
              <strong>Este es tu último escaneo gratuito.</strong> Con el acceso Pro tienes escaneos ilimitados por un pago único.
            </>
          ) : (
            <>
              <strong>Te queda 1 escaneo gratis</strong> en los próximos 30 días. Con el Pro tienes escaneos ilimitados.
            </>
          )}
        </p>
        <Link
          to="/modulo-pro"
          data-testid="free-quota-cta"
          className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold underline-offset-4 hover:underline shrink-0"
        >
          Desbloquear — $14.990
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

function LimitReachedPanel({ info, onReset }) {
  const [loading, setLoading] = useState(false);
  const goPagar = async () => {
    setLoading(true);
    trackEvent("limit_cta_click", { plan: "pro_pago_unico" });
    try {
      const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (!r?.data?.id) throw new Error("no-session");
      const { data } = await axios.post(`${API}/pro/checkout/transbank`, {}, { withCredentials: true });
      if (data?.buy_order === "ALREADY_PRO") {
        window.location.href = "/cuenta";
        return;
      }
      if (data?.url && data?.token) {
        redirectToWebpay(data.url, data.token);
        return;
      }
      toast.error("Webpay no devolvió el link. Reintenta.");
    } catch (err) {
      if (err?.message === "no-session" || err?.response?.status === 401) {
        window.location.href = "/registro?checkout=transbank";
        return;
      }
      toast.error(err?.response?.data?.detail || "No pudimos iniciar el pago. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="px-6 py-16 sm:py-24" data-testid="limit-reached-panel">
      <div className="max-w-3xl mx-auto">
        <div className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white rounded-2xl p-8 sm:p-12 shadow-xl relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold uppercase tracking-wide">
              <Lock className="w-3.5 h-3.5" />
              Límite gratuito alcanzado
            </div>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Ya usaste tus {info.limit} escaneos gratuitos
            </h1>
            <p className="mt-3 text-white/85 max-w-xl text-base leading-relaxed">
              Llevas {info.scans_usados} escaneos en los últimos {info.ventana_dias} días.
              Con un pago único de <strong className="text-white">$14.990 CLP</strong> desbloqueas escaneos ilimitados,
              los 7 documentos legales de la Ley 21.719 y las alertas de la ley — para siempre.
            </p>

            <div className="mt-8 max-w-md bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Acceso Pro · pago único</p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-4xl font-bold">$14.990</span>
                <span className="text-sm text-white/70">CLP · una vez</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-white/90">
                <li className="flex items-center gap-2"><InfinityIcon className="w-3.5 h-3.5 text-emerald-300" /><strong>Escaneos ilimitados</strong></li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/70" />7 documentos legales conforme a Ley 21.719</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/70" />Descarga en PDF, Markdown y TXT</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/70" /><strong>Alertas de la ley gratis de por vida</strong></li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-white/70" />Acceso permanente · sin cobros futuros</li>
              </ul>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={goPagar}
                disabled={loading}
                data-testid="limit-cta-pro"
                className="h-12 px-7 rounded-lg bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 active:scale-[0.98] disabled:opacity-60 transition-all inline-flex items-center gap-2"
              >
                {loading ? "Iniciando pago…" : (<>Desbloquear todo — $14.990 <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <Link
                to="/login"
                data-testid="limit-cta-login"
                className="h-12 px-5 rounded-lg border border-white/30 text-white hover:bg-white/10 font-medium inline-flex items-center"
              >
                Ya tengo cuenta Pro
              </Link>
              <button
                onClick={onReset}
                data-testid="limit-cta-back"
                className="h-12 px-5 rounded-lg text-white/80 hover:text-white hover:bg-white/10 font-medium"
              >
                Volver al inicio
              </button>
            </div>
            <p className="mt-6 text-xs text-white/60">
              Pago único · Webpay · sin renovación · sin letra chica.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ClosingCTA({ onScrollToScanner }) {
  return (
    <section data-testid="closing-cta" className="px-6 py-20 bg-gradient-to-br from-[#0b1220] to-[#1E3A8A] text-white text-center border-t border-white/5">
      <div className="max-w-2xl mx-auto">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          El 1 de diciembre de 2026 la ley entra en vigencia.
        </h2>
        <p className="mt-3 text-blue-100 text-base sm:text-lg">
          Escanea tu sitio gratis hoy. Cuando estés listo, desbloquea todo por un pago único de $14.990.
        </p>
        <button
          onClick={onScrollToScanner}
          data-testid="closing-cta-scan"
          className="mt-8 h-12 px-7 rounded-xl bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 active:scale-[0.98] inline-flex items-center gap-2 transition"
        >
          Escanear mi sitio
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}
